import { Component, ChangeDetectionStrategy, computed, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TagModule } from 'primeng/tag';
import { InputMaskModule } from 'primeng/inputmask';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { SubscriptionService } from '../../../services/subscription.service';
import { PublicSaleLinkDTO, SaleLinkService } from '../../../services/sale-link.service';
import {
  type PlanItemDTO,
  type PublicSubscriptionCheckoutRequest,
  type PublicSubscriptionDetailDTO,
  type SATCFDIUseDTO,
  type SubscriptionDTO
} from '../../../core/models';

/** Payload de checkout Paycode (nombre del titular partido en tres campos). */
interface PaycodeSubscriptionCheckoutRequest {
  customerName: string;
  customerMiddleName: string;
  customerLastName?: string;
  customerEmail: string;
  customerPhone?: string;
  cardNumber: string;
  cardholderName: string;
  expMonth: string;
  expYear: string;
  cvv?: string;
  userAgent?: string;
  cfdiUseId?: string;
}

@Component({
  selector: 'app-public-paycode-subscription-checkout',
  templateUrl: './public-paycode-subscription-checkout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TagModule,
    InputMaskModule,
    FloatLabelModule,
    SelectModule
  ]
})
export class PublicPaycodeSubscriptionCheckoutComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private subscriptionService = inject(SubscriptionService);
  private saleLinkService = inject(SaleLinkService);
  private fb = inject(FormBuilder);

  /** Liga de pago de tipo suscripción. Null cuando el checkout es de una suscripción ya creada. */
  private saleLinkId: number | null = null;
  private saleLinkHash: string | null = null;

  detail = signal<PublicSubscriptionDetailDTO | null>(null);
  subscription = signal<SubscriptionDTO | null>(null);
  loading = signal(true);
  error = signal(false);
  errorMessage = signal('');
  checkoutLoading = signal(false);
  checkoutSuccess = signal(false);
  userAgent = '';
  satCfdiUseOptions = signal<SATCFDIUseDTO[]>([]);
  catalogsLoading = signal(false);

  satCfdiUseSelectOptions = computed(() =>
    this.satCfdiUseOptions()
      .filter((use) => use.id != null)
      .map((use) => ({
        id: use.id!,
        label: this.formatCfdiUseLabel(use)
      }))
  );

  checkoutForm: FormGroup;

  constructor() {
    this.checkoutForm = this.fb.group({
      requiresInvoice: [false],
      cfdiUseId: [null as string | null],
      cardholderFirstName: ['', [Validators.required, Validators.maxLength(200)]],
      cardholderMiddleName: ['', [Validators.required, Validators.maxLength(200)]],
      cardholderLastName: ['', [Validators.maxLength(200)]],
      cardNumber: ['', [Validators.required, Validators.minLength(13), Validators.maxLength(19)]],
      phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      cvv: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]],
      expirationDate: ['', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/)]]
    });

    this.checkoutForm.get('requiresInvoice')?.valueChanges.subscribe((requires: boolean) => {
      this.applyInvoiceValidators(requires);
    });
  }

  ngOnInit(): void {
    this.userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

    const saleLinkId = this.route.snapshot.paramMap.get('id');
    if (saleLinkId) {
      this.loadSaleLink(Number(saleLinkId), this.route.snapshot.queryParamMap.get('hash'));
      return;
    }

    const token = this.route.snapshot.paramMap.get('token');

    if (!token) {
      this.loading.set(false);
      this.error.set(true);
      return;
    }

    this.subscriptionService.getPublicSubscription(token).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (!res?.body) {
          this.error.set(true);
          return;
        }
        this.detail.set(res.body);
        this.subscription.set(res.body.subscription ?? null);
        this.initInvoiceFormFromSubscription(res.body.subscription ?? null);
        this.loadPublicBillingCatalogs(token);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      }
    });
  }

  private loadSaleLink(id: number, hash: string | null): void {
    if (!id || Number.isNaN(id) || !hash) {
      this.loading.set(false);
      this.error.set(true);
      this.errorMessage.set('Verifica que la URL sea correcta.');
      return;
    }
    this.saleLinkId = id;
    this.saleLinkHash = hash;
    this.saleLinkService.getPublic(id, hash).subscribe({
      next: (res) => {
        const link = res?.body;
        if (!link) {
          this.loading.set(false);
          this.error.set(true);
          this.errorMessage.set('Verifica que la URL sea correcta.');
          return;
        }
        if (link.type !== 'SUBSCRIPTION') {
          void this.router.navigate(['/public/sale-links', id, 'receipt'], {
            queryParams: { hash },
            replaceUrl: true
          });
          return;
        }
        if (link.provider && link.provider !== 'PAYCODE') {
          this.loading.set(false);
          this.error.set(true);
          this.errorMessage.set('Esta liga de suscripción no se puede pagar con tarjeta Paycode.');
          return;
        }
        const subscription = this.saleLinkAsSubscription(link);
        this.detail.set({
          subscription,
          companyName: link.companyName,
          companyLogoUrl: link.companyLogoUrl,
          provider: 'PAYCODE'
        });
        this.subscription.set(subscription);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
        this.errorMessage.set('No se pudo cargar la suscripción. Verifica el enlace e intenta de nuevo.');
      }
    });
  }

  /** Adapta la liga al mismo modelo que usa el checkout de una suscripción existente. */
  private saleLinkAsSubscription(link: PublicSaleLinkDTO): SubscriptionDTO {
    const status = link.status === 'PAID'
      ? 'ACTIVE'
      : link.status === 'CANCELLED'
        ? 'CANCELLED'
        : link.status === 'EXPIRED'
          ? 'EXPIRED'
          : 'PENDING_PAYMENT';
    return {
      status,
      frequency: link.frequency,
      currency: link.currencyCode || 'MXN',
      subtotal: link.subtotal,
      tax: link.tax,
      discount: 0,
      total: link.total,
      notes: link.concept,
      customerName: link.customerName,
      customerEmail: link.customerEmail,
      customer: {
        comercialName: link.customerName,
        legalName: link.customerName,
        email: link.customerEmail,
        phone: link.customerPhone
      },
      plan: {
        name: link.concept || 'Suscripción',
        items: (link.lines ?? []).map((line, index) => ({
          id: index,
          description: line.name,
          quantity: line.quantity,
          totalAmount: line.total ?? line.subtotal
        }))
      }
    };
  }

  private initInvoiceFormFromSubscription(sub: SubscriptionDTO | null): void {
    const bt = sub?.billingTerms;
    if (!bt?.cfdiUse?.id) {
      return;
    }
    this.checkoutForm.patchValue({
      requiresInvoice: true,
      cfdiUseId: bt.cfdiUse.id
    }, { emitEvent: true });
  }

  private loadPublicBillingCatalogs(token: string): void {
    this.catalogsLoading.set(true);
    this.subscriptionService.listPublicSatCfdiUses(token).subscribe({
      next: (r) => {
        this.satCfdiUseOptions.set(r.ok && r.body ? r.body : []);
        this.catalogsLoading.set(false);
      },
      error: () => {
        this.satCfdiUseOptions.set([]);
        this.catalogsLoading.set(false);
      }
    });
  }

  private applyInvoiceValidators(requiresInvoice: boolean): void {
    const cfdiUseId = this.checkoutForm.get('cfdiUseId');

    if (requiresInvoice) {
      cfdiUseId?.setValidators([Validators.required]);
    } else {
      cfdiUseId?.clearValidators();
      cfdiUseId?.setValue(null, { emitEvent: false });
    }

    cfdiUseId?.updateValueAndValidity({ emitEvent: false });
  }

  get requiresInvoice(): boolean {
    return !!this.checkoutForm.get('requiresInvoice')?.value;
  }

  formatCfdiUseLabel(use: SATCFDIUseDTO): string {
    const desc = use.description?.trim() || use.name?.trim() || '—';
    return desc;
  }

  isCheckoutFormValid(): boolean {
    if (this.requiresInvoice && !this.checkoutForm.get('cfdiUseId')?.valid) {
      return false;
    }
    const cardFields = [
      'cardholderFirstName',
      'cardholderMiddleName',
      'cardNumber',
      'phone',
      'expirationDate',
      'cvv'
    ] as const;
    return cardFields.every(name => this.checkoutForm.get(name)?.valid);
  }

  async onCheckout(): Promise<void> {
    this.checkoutLoading.set(true);
    this.errorMessage.set('');

    if (!this.isCheckoutFormValid()) {
      this.checkoutForm.markAllAsTouched();
      this.errorMessage.set(
        this.requiresInvoice
          ? 'Revisa los datos de facturación y de la tarjeta antes de continuar.'
          : 'Revisa los campos de la tarjeta antes de continuar.');
      this.checkoutLoading.set(false);
      return;
    }

    try {
      const checkoutRequest = this.buildCheckoutRequest();
      const subscriptionToken = this.route.snapshot.paramMap.get('token')!;
      await this.submitCheckout(subscriptionToken, checkoutRequest);
    } catch (err: unknown) {
      this.checkoutLoading.set(false);
      this.errorMessage.set(err instanceof Error ? err.message : 'No se pudo preparar el pago.');
    }
  }

  private buildCheckoutRequest(): PaycodeSubscriptionCheckoutRequest {
    const sub = this.subscription();
    const cvvVal = String(this.checkoutForm.get('cvv')?.value ?? '').trim();
    const raw = this.checkoutForm.getRawValue() as {
      requiresInvoice?: boolean;
      cfdiUseId?: string | null;
      cardholderFirstName?: string;
      cardholderMiddleName?: string;
      cardholderLastName?: string;
      cardNumber?: string;
      phone?: string;
      expirationDate?: string;
    };

    const firstName = String(raw.cardholderFirstName ?? '').trim();
    const middleName = String(raw.cardholderMiddleName ?? '').trim();
    const lastName = String(raw.cardholderLastName ?? '').trim();
    const fallbackName = this.subscriberName(sub);
    const fallbackParts = fallbackName.split(/\s+/).filter(Boolean);

    const cardNumber = String(raw.cardNumber ?? '').replace(/\s+/g, '');
    const phone = String(raw.phone ?? '').replace(/\D/g, '');
    const expiration = String(raw.expirationDate ?? '').trim();
    const [expMonth = '', expYearShort = ''] = expiration.split('/');
    const expYear = expYearShort.length === 2 ? `20${expYearShort}` : expYearShort;
    const cardholderName = [firstName, middleName, lastName].filter(Boolean).join(' ');

    if (!cardNumber || !expMonth || !expYear || !cardholderName) {
      throw new Error('Revisa los campos de la tarjeta antes de continuar.');
    }
    if (!phone || phone.length !== 10) {
      throw new Error('El teléfono debe tener 10 dígitos.');
    }

    return {
      customerName: firstName || fallbackParts[0] || fallbackName || 'Cliente',
      customerMiddleName: middleName || fallbackParts[1] || fallbackParts[0] || 'NA',
      customerLastName: lastName || fallbackParts.slice(2).join(' ') || undefined,
      customerEmail: this.subscriberEmail(sub),
      customerPhone: phone,
      cardNumber,
      cardholderName,
      expMonth,
      expYear,
      cvv: cvvVal,
      userAgent: this.userAgent || undefined,
      ...(raw.requiresInvoice && raw.cfdiUseId != null
        ? { cfdiUseId: String(raw.cfdiUseId) }
        : {})
    };
  }

  private async submitCheckout(
    subscriptionToken: string,
    checkoutRequest: PaycodeSubscriptionCheckoutRequest
  ): Promise<void> {
    try {
      const previous = this.subscription();
      const res = await firstValueFrom(
        this.saleLinkId != null && this.saleLinkHash
          ? this.saleLinkService.checkoutSubscription(
              this.saleLinkId,
              this.saleLinkHash,
              checkoutRequest as PublicSubscriptionCheckoutRequest
            )
          : this.subscriptionService.checkoutPaycodeSubscription(
              subscriptionToken,
              checkoutRequest as PublicSubscriptionCheckoutRequest
            )
      );

      const body = res?.body;
      if (!body) {
        this.errorMessage.set(res?.message ?? 'No se pudo completar la suscripción.');
        this.checkoutLoading.set(false);
        return;
      }

      const merged: SubscriptionDTO = {
        ...previous,
        ...body,
        plan: body.plan ?? previous?.plan,
        customer: body.customer ?? previous?.customer,
        notes: body.notes || previous?.notes,
        frequency: body.frequency ?? previous?.frequency
      };
      this.subscription.set(merged);
      const d = this.detail();
      if (d) {
        this.detail.set({ ...d, subscription: merged });
      }

      this.checkoutSuccess.set(true);
      this.checkoutLoading.set(false);
    } catch (err: unknown) {
      const e = err as { error?: { message?: string }; message?: string } | null;
      this.errorMessage.set(e?.error?.message ?? e?.message ?? 'No se pudo completar el pago.');
      this.checkoutLoading.set(false);
    }
  }

  /** Nombre visible: el API ya no envía `customerName` plano, solo `customer`. */
  subscriberName(sub: SubscriptionDTO | null | undefined): string {
    const customer = sub?.customer;
    return (
      customer?.comercialName?.trim()
      || customer?.legalName?.trim()
      || sub?.customerName?.trim()
      || ''
    );
  }

  /** Correo del suscriptor: el API lo entrega en `customer.email`. */
  subscriberEmail(sub: SubscriptionDTO | null | undefined): string {
    return sub?.customer?.email?.trim() || sub?.customerEmail?.trim() || '';
  }

  hasSubscriberContact(sub: SubscriptionDTO | null | undefined): boolean {
    return !!this.subscriberName(sub) && !!this.subscriberEmail(sub);
  }

  get isPendingPayment(): boolean {
    return this.subscription()?.status === 'PENDING_PAYMENT';
  }

  get isActive(): boolean {
    return this.subscription()?.status === 'ACTIVE';
  }

  get isCancelled(): boolean {
    const s = this.subscription()?.status;
    return s === 'CANCELLED' || s === 'CANCELED' || s === 'EXPIRED';
  }

  get planItems(): PlanItemDTO[] {
    const fromPlan = this.subscription()?.plan?.items ?? [];
    if (fromPlan.length > 0) {
      return fromPlan;
    }
    return (this.subscription()?.lines ?? []).map((line) => ({
      id: line.id,
      description: line.description,
      quantity: line.quantity,
      totalAmount: line.totalAmount
    }));
  }

  get currency(): string {
    return this.subscription()?.currency ?? 'MXN';
  }

  onPhoneKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key;
    if (key.length !== 1) return;
    if (key < '0' || key > '9') {
      event.preventDefault();
    }
  }

  onPhoneInput(): void {
    const c = this.checkoutForm.get('phone');
    if (!c) return;
    const digits = String(c.value ?? '').replace(/\D/g, '').slice(0, 10);
    if (digits !== c.value) {
      c.setValue(digits, { emitEvent: false });
      c.updateValueAndValidity();
    }
  }

  onExpirationKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key;
    if (key.length !== 1 || key < '0' || key > '9') return;

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? start;
    const val = input.value;

    if (start < 3 && end > start) {
      return;
    }

    if (start >= 3) return;

    const first = val.charAt(0);

    if (start === 0) {
      if (key !== '0' && key !== '1') {
        event.preventDefault();
      }
      return;
    }

    if (start === 1 && end === 1) {
      if (!/\d/.test(first)) return;
      if (first === '0') {
        if (key === '0') event.preventDefault();
      } else if (first === '1') {
        if (key > '2') event.preventDefault();
      }
    }
  }

  onExpirationInput(): void {
    const c = this.checkoutForm.get('expirationDate');
    if (!c) return;
    const raw = (c.value as string) ?? '';
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 2) return;

    const origMm = parseInt(digits.slice(0, 2), 10);
    if (origMm >= 1 && origMm <= 12) return;

    let mm = origMm;
    if (mm > 12) mm = 12;
    if (mm < 1) mm = 1;
    const mmStr = mm.toString().padStart(2, '0');
    const yy = digits.slice(2, 4);

    let desired: string;
    if (digits.length >= 4) {
      desired = `${mmStr}/${yy}`;
    } else if (digits.length === 3) {
      desired = `${mmStr}/${digits.slice(2)}`;
    } else {
      desired = `${mmStr}/`;
    }

    if (desired !== raw) {
      c.setValue(desired, { emitEvent: false });
      c.updateValueAndValidity();
    }
  }

  formatAmount(amount: number | undefined, curr?: string): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: curr || this.currency || 'MXN'
    }).format(amount ?? 0);
  }

  formatDate(isoDate: string | undefined): string {
    if (!isoDate) return '—';
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(isoDate));
  }

  getFrequencyLabel(freq: string | undefined): string {
    const labels: Record<string, string> = {
      ONCE: 'Único',
      DAILY: 'Diario',
      WEEKLY: 'Semanal',
      BIWEEKLY: 'Quincenal',
      MONTHLY: 'Mensual',
      QUARTERLY: 'Trimestral',
      SEMIANNUALLY: 'Semestral',
      ANNUALLY: 'Anual'
    };
    return freq ? labels[freq] ?? freq : '—';
  }

  getStatusLabel(status: string | undefined): string {
    const labels: Record<string, string> = {
      PENDING_PAYMENT: 'Pendiente de pago',
      ACTIVE: 'Activa',
      PAUSED: 'Pausada',
      CANCELLED: 'Cancelada',
      EXPIRED: 'Expirada'
    };
    return status ? labels[status] ?? status : '—';
  }
}
